
import React, { createContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const TaskContext = createContext();

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState(() => {
    const savedTasks = localStorage.getItem('timeLogger_tasks');
    if (savedTasks) {
      const parsedTasks = JSON.parse(savedTasks);
      // Migrate old tasks to ensure they have all required properties
      return parsedTasks.map((task, index) => ({
        ...task,
        removed: task.removed ?? false,
        addedDatetime: task.addedDatetime ?? new Date().toISOString(),
        startDatetime: task.startDatetime ?? null,
        finishDatetime: task.finishDatetime ?? null,
        sortOrder: task.sortOrder ?? index
      }));
    }
    return [];
  });
  const [selectedTask, setSelectedTask] = useState(() => {
    const savedSelectedTask = localStorage.getItem('timeLogger_selectedTask');
    return savedSelectedTask ? JSON.parse(savedSelectedTask) : null;
  });

  useEffect(() => {
    localStorage.setItem('timeLogger_tasks', JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    localStorage.setItem('timeLogger_selectedTask', JSON.stringify(selectedTask));
  }, [selectedTask]);

  const addTask = (taskName) => {
    const activeTasks = tasks.filter(task => !task.completed && !task.removed);
    const maxSortOrder = activeTasks.length > 0 ? Math.max(...activeTasks.map(t => t.sortOrder || 0)) : -1;
    
    const newTask = { 
      id: uuidv4(), 
      name: taskName, 
      completed: false,
      removed: false,
      addedDatetime: new Date().toISOString(),
      startDatetime: null,
      finishDatetime: null,
      sortOrder: maxSortOrder + 1
    };
    setTasks((prevTasks) => [...prevTasks, newTask]);
  };

  const reorderTasks = (startIndex, endIndex) => {
    // Get only active tasks (not completed and not removed) sorted by current sortOrder
    const activeTasks = tasks
      .filter(task => !task.completed && !task.removed)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const otherTasks = tasks.filter(task => task.completed || task.removed);
    
    // Reorder only the active tasks
    const reorderedActive = Array.from(activeTasks);
    const [movedTask] = reorderedActive.splice(startIndex, 1);
    reorderedActive.splice(endIndex, 0, movedTask);
    
    // Update sortOrder values to reflect new positions
    const updatedActive = reorderedActive.map((task, index) => ({
      ...task,
      sortOrder: index
    }));
    
    // Combine reordered active tasks with other tasks
    setTasks([...updatedActive, ...otherTasks]);
  };

  const selectTask = (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (task && !task.startDatetime) {
      const updatedTask = { ...task, startDatetime: new Date().toISOString() };
      setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      setSelectedTask(updatedTask);
    } else {
      setSelectedTask(task);
    }
  };

  const clearSelectedTask = () => {
    setSelectedTask(null);
  };

  const finishSelectedTask = () => {
    if (selectedTask) {
      const finishDatetime = new Date().toISOString();
      setTasks(tasks.map(t => t.id === selectedTask.id ? {...t, completed: true, finishDatetime} : t));
      setSelectedTask(null);
    }
  };

  const deleteTask = (taskId) => {
    setTasks(tasks.filter(t => t.id !== taskId));
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(null);
    }
  };

  const importTasks = (importedTasks) => {
    setTasks(importedTasks);
  };

  const exportTasks = () => {
    return {
      tasks,
      selectedTask,
      exportDate: new Date().toISOString(),
    };
  };

  const bulkDeleteTasks = (taskIds) => {
    setTasks(tasks.filter(t => !taskIds.includes(t.id)));
    if (selectedTask && taskIds.includes(selectedTask.id)) {
      setSelectedTask(null);
    }
  };

  const bulkCompleteTasks = (taskIds) => {
    const finishDatetime = new Date().toISOString();
    setTasks(tasks.map(t => 
      taskIds.includes(t.id) ? {...t, completed: true, finishDatetime} : t
    ));
    if (selectedTask && taskIds.includes(selectedTask.id)) {
      setSelectedTask(null);
    }
  };

  const bulkRemoveTasks = (taskIds) => {
    setTasks(tasks.map(t => 
      taskIds.includes(t.id) ? {...t, removed: true} : t
    ));
    if (selectedTask && taskIds.includes(selectedTask.id)) {
      setSelectedTask(null);
    }
  };

  const updateTaskName = (taskId, newName) => {
    setTasks(tasks.map(t => 
      t.id === taskId ? {...t, name: newName} : t
    ));
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask({...selectedTask, name: newName});
    }
  };

  const updateTaskStatus = (taskId, status) => {
    const now = new Date().toISOString();
    let updateObj = {};
    
    switch (status) {
      case 'New':
        updateObj = { completed: false, removed: false, startDatetime: null, finishDatetime: null };
        break;
      case 'Started':
        updateObj = { completed: false, removed: false, startDatetime: now, finishDatetime: null };
        break;
      case 'Completed':
        updateObj = { completed: true, removed: false, finishDatetime: now };
        break;
      case 'Removed':
        updateObj = { removed: true };
        break;
      default:
        updateObj = {};
        break;
    }
    
    setTasks(tasks.map(t => 
      t.id === taskId ? {...t, ...updateObj} : t
    ));
    
    if (selectedTask && selectedTask.id === taskId) {
      if (status === 'Completed' || status === 'Removed') {
        setSelectedTask(null);
      } else {
        setSelectedTask({...selectedTask, ...updateObj});
      }
    }
  };

  return (
    <TaskContext.Provider
      value={{
        tasks,
        selectedTask,
        addTask,
        reorderTasks,
        selectTask,
        clearSelectedTask,
        finishSelectedTask,
        deleteTask,
        importTasks,
        exportTasks,
        bulkDeleteTasks,
        bulkCompleteTasks,
        bulkRemoveTasks,
        updateTaskName,
        updateTaskStatus,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};
